import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { getTenantInfo } from '../lib/storage'
import ScreenBadge from '../components/ScreenBadge'
import { screenApi } from '../lib/api'
import { formatClock, formatClassTime } from '../lib/utils'
import type { ClassInstance } from '@gymboard/types'
import GymLeaderboard from '../components/idle/GymLeaderboard'
import { BOARD_METRICS, FLOOR_BOARD_METRICS, useGymBoards } from '../hooks/useGymBoards'
import FloorCheckInMini from '../components/visits/FloorCheckInMini'
import { useVisitQr } from '../hooks/useVisitQr'
import AdCard from '../components/idle/AdCard'
import { useAdRotation, useScreenAds } from '../hooks/useScreenAds'
import { useScreenFeatures } from '../hooks/useScreenFeatures'

const QUOTES = [
  'Every rep counts. Every drop of sweat matters.',
  'The pain you feel today will be the strength you feel tomorrow.',
  'Push harder than yesterday if you want a different tomorrow.',
  'Champions aren\'t born. They\'re built — one workout at a time.',
  'Your only competition is who you were yesterday.',
  'Embrace the burn. That\'s your body getting stronger.',
  'Train insane or remain the same.',
  'The clock is ticking. Make it count.',
  'Hard work beats talent when talent doesn\'t work hard.',
  'Show up. Give everything. No excuses.',
]

interface Props {
  roomId: string | null
}

export default function AmbientScreen({ roomId }: Props) {
  const [clock, setClock] = useState(() => formatClock(new Date()))
  const [quoteIndex, setQuoteIndex] = useState(0)
  const [showQuote, setShowQuote] = useState(true)
  const [nextClass, setNextClass] = useState<ClassInstance | null>(null)
  const tenantInfo = getTenantInfo()
  // Gym-floor check-in: when the gym has it on, the board leads with visits
  // and the check-in code takes the quote's place.
  const { qr: visitQr } = useVisitQr()
  // Per-gym features: boards and ads leave the screen when switched off.
  const features = useScreenFeatures()
  const metrics = visitQr ? FLOOR_BOARD_METRICS : BOARD_METRICS
  const boards = useGymBoards(8, metrics, features?.gym_leaderboard !== false)
  const hasBoard = metrics.some((m) => (boards[m]?.entries.length ?? 0) > 0)
  const screenAds = useScreenAds(features?.ads !== false)
  const hasAds = screenAds.length > 0
  const rotation = useAdRotation(screenAds)
  const gymName = tenantInfo?.name ?? 'From the gym'

  // Clock tick
  useEffect(() => {
    const interval = setInterval(() => {
      setClock(formatClock(new Date()))
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  // Quote rotation every 10s
  useEffect(() => {
    const interval = setInterval(() => {
      setShowQuote(false)
      setTimeout(() => {
        setQuoteIndex((i) => (i + 1) % QUOTES.length)
        setShowQuote(true)
      }, 500)
    }, 10_000)
    return () => clearInterval(interval)
  }, [])

  // Fetch upcoming classes
  const fetchNextClass = async () => {
    if (!roomId) return
    try {
      const res = await screenApi.getUpcomingClasses(roomId)
      const items = Array.isArray(res.data)
        ? res.data
        : (res.data as any)?.data ?? []
      if (items.length > 0) {
        setNextClass(items[0] as ClassInstance)
      }
    } catch {
      // silently ignore
    }
  }

  useEffect(() => {
    fetchNextClass()
    const interval = setInterval(fetchNextClass, 30_000)
    return () => clearInterval(interval)
  }, [roomId])

  // Poll for active session every 30s
  useEffect(() => {
    if (!roomId) return
    const poll = async () => {
      try {
        const res = await screenApi.getActiveSession(roomId)
        if (res.data && (res.data as any).id) {
          window.location.reload()
        }
      } catch {
        // ignore
      }
    }
    const interval = setInterval(poll, 30_000)
    return () => clearInterval(interval)
  }, [roomId])

  const className = nextClass?.template?.name ?? nextClass?.template?.name ?? null
  const coachName = nextClass?.coach
    ? `${nextClass.coach.firstName} ${nextClass.coach.lastName}`
    : null
  const scheduledAt = nextClass?.scheduledAt ? formatClassTime(nextClass.scheduledAt) : null
  const capacity = nextClass?.capacity ?? null
  const bookingCount = nextClass?.bookingCount ?? 0

  return (
    <div className="w-screen h-screen overflow-hidden relative bg-[#0E1022] flex flex-col">
      {/* Animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(ellipse 120% 80% at 20% 50%, rgba(99,102,241,0.12) 0%, transparent 60%), radial-gradient(ellipse 80% 100% at 80% 20%, rgba(201,58,48,0.08) 0%, transparent 60%)',
          }}
        />
        {/* Subtle moving orbs */}
        <motion.div
          className="absolute w-[600px] h-[600px] rounded-full opacity-5"
          style={{
            background: 'radial-gradient(circle, #6366f1, transparent)',
            top: '10%',
            left: '-10%',
          }}
          animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute w-[400px] h-[400px] rounded-full opacity-5"
          style={{
            background: 'radial-gradient(circle, #C93A30, transparent)',
            bottom: '10%',
            right: '-5%',
          }}
          animate={{ x: [0, -20, 0], y: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Top bar: gym name + clock */}
      <div className="relative z-10 flex items-start justify-between px-16 pt-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-white/30 text-sm font-semibold tracking-[0.2em] uppercase mb-1">
            {tenantInfo?.name ?? 'Agaru'}
          </div>
          <div className="text-white/15 text-sm">
            No active session
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="text-right"
        >
          <div
            className="text-8xl font-black text-white/80 tabular-nums tracking-tight"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {clock}
          </div>
          <div className="text-white/30 text-sm tracking-[0.15em] uppercase mt-1">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </div>
        </motion.div>
      </div>

      {hasBoard || hasAds ? (
        /* Two columns. Left: next class, then an ad (when the board takes the
           right) or the quote. Right: this week's gym board, else the ad. */
        <div className="relative z-10 flex-1 min-h-0 flex gap-[2vw] px-16 pt-8 pb-14">
          <div className="w-[36%] flex-none flex flex-col justify-center gap-10">
            <AnimatePresence mode="wait">
              {nextClass ? (
                <motion.div
                  key="next-class"
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ duration: 0.5 }}
                  className="bg-[#191C33]/80 border border-[#31365A] rounded-3xl p-10 max-w-2xl w-full backdrop-blur-sm"
                >
                  <div className="text-orange-300/70 text-sm font-semibold tracking-[0.2em] uppercase mb-4">
                    Next Class
                  </div>
                  <h2 className="text-4xl font-black text-white mb-3 leading-tight">
                    {className ?? 'Upcoming Class'}
                  </h2>
                  {scheduledAt && (
                    <div className="text-white/50 text-2xl font-medium mb-4">
                      {scheduledAt}
                    </div>
                  )}
                  <div className="flex items-center gap-6 text-white/40 text-lg">
                    {coachName && (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {coachName}
                      </span>
                    )}
                    {capacity && (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        {bookingCount} / {capacity} spots
                      </span>
                    )}
                    {nextClass.durationMins && (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {nextClass.durationMins} min
                      </span>
                    )}
                  </div>

                  {/* Capacity bar */}
                  {capacity && capacity > 0 && (
                    <div className="mt-6">
                      <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                        <motion.div
                          className="h-full bg-orange-500/60 rounded-full"
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min(100, (bookingCount / capacity) * 100)}%` }}
                          transition={{ duration: 1, delay: 0.3 }}
                        />
                      </div>
                    </div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="no-class"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="text-center"
                >
                  <div className="text-8xl mb-8 opacity-30">🏋️</div>
                  <h2 className="text-4xl font-bold text-white/30">
                    Ready when you are
                  </h2>
                  <p className="text-white/20 text-xl mt-3">
                    No upcoming classes scheduled
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {visitQr ? (
              <FloorCheckInMini qr={visitQr} />
            ) : hasBoard && hasAds ? (
              <AdCard ads={screenAds} ad={rotation.ad} index={rotation.index} gymName={gymName} size="compact" />
            ) : (
              <div>
                <AnimatePresence mode="wait">
                  {showQuote && (
                    <motion.p
                      key={quoteIndex}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.5 }}
                      className="text-white/25 text-xl font-light italic"
                    >
                      "{QUOTES[quoteIndex]}"
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
          <motion.div
            className="flex-1 min-w-0"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
          >
            {hasBoard ? (
              <GymLeaderboard boards={boards} metrics={metrics} />
            ) : (
              <AdCard ads={screenAds} ad={rotation.ad} index={rotation.index} gymName={gymName} size="hero" />
            )}
          </motion.div>
        </div>
      ) : (
        <>
        {/* Center: Next class card */}
        <div className="relative z-10 flex-1 flex items-center justify-center px-20">
          <AnimatePresence mode="wait">
            {nextClass ? (
              <motion.div
                key="next-class"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                className="bg-[#191C33]/80 border border-[#31365A] rounded-3xl p-12 max-w-2xl w-full backdrop-blur-sm"
              >
                <div className="text-orange-300/70 text-sm font-semibold tracking-[0.2em] uppercase mb-4">
                  Next Class
                </div>
                <h2 className="text-5xl font-black text-white mb-3 leading-tight">
                  {className ?? 'Upcoming Class'}
                </h2>
                {scheduledAt && (
                  <div className="text-white/50 text-2xl font-medium mb-4">
                    {scheduledAt}
                  </div>
                )}
                <div className="flex items-center gap-6 text-white/40 text-lg">
                  {coachName && (
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {coachName}
                    </span>
                  )}
                  {capacity && (
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {bookingCount} / {capacity} spots
                    </span>
                  )}
                  {nextClass.durationMins && (
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {nextClass.durationMins} min
                    </span>
                  )}
                </div>

                {/* Capacity bar */}
                {capacity && capacity > 0 && (
                  <div className="mt-6">
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-orange-500/60 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min(100, (bookingCount / capacity) * 100)}%` }}
                        transition={{ duration: 1, delay: 0.3 }}
                      />
                    </div>
                  </div>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="no-class"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                <div className="text-8xl mb-8 opacity-30">🏋️</div>
                <h2 className="text-4xl font-bold text-white/30">
                  Ready when you are
                </h2>
                <p className="text-white/20 text-xl mt-3">
                  No upcoming classes scheduled
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom: the floor check-in code when it's on, else a rotating quote */}
        <div className="relative z-10 px-20 pb-12">
          {visitQr ? (
            <div className="max-w-2xl mx-auto">
              <FloorCheckInMini qr={visitQr} />
            </div>
          ) : null}
          <AnimatePresence mode="wait">
            {!visitQr && showQuote && (
              <motion.p
                key={quoteIndex}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5 }}
                className="text-center text-white/25 text-2xl font-light italic max-w-3xl mx-auto"
              >
                "{QUOTES[quoteIndex]}"
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        </>
      )}

      {/* Agaru branding bottom-right */}
      <div className="absolute bottom-8 right-10 text-white/15 text-sm font-semibold tracking-widest uppercase z-10">
        Agaru
      </div>

      {/* Screen badge — tells you which TV this is */}
      <div className="absolute top-5 left-5 z-20">
        <ScreenBadge className="text-white/25 text-[10px]" />
      </div>
    </div>
  )
}
